import styled from "styled-components";
import { MdLaunch } from "react-icons/md";
import { Flex, Text } from "rebass/styled-components";
import { useRecoilState, useRecoilValue } from "recoil";
import { useTranslation } from "react-i18next";

import { useAnalyticsContext } from "MVVM/Hooks/useAnalyticsContext";
import { Button } from "MVVM/Components/Button";
import { ActiveOverlayPopUpState, PlatformState } from "MVVM/State/iframe";
import { buildLink } from "MVVM/Utilities/Player";
import { IClip } from "MVVM/@types";
import { getMatchHistoryGameEnum } from "MVVM/Views/MatchHistory/utils";
import { CloseButton } from "../../styledComponents";
import "./index.css";

export const EditPopUp = ({ clip }: { clip: IClip }) => {
  const platform = useRecoilValue(PlatformState);
  const { track } = useAnalyticsContext();
  const { t } = useTranslation("translation", {
    keyPrefix: "embeddablePlayer.editPopUp",
  });
  const [activeOverlayPopUp, setActiveOverlayPopUp] = useRecoilState(
    ActiveOverlayPopUpState,
  );
  const title = (
    <>
      {t("title", "Edit Your Clips with")} <span>Allstar.gg</span>
    </>
  );

  const copy = t(
    "copy",
    "Go to Allstar.gg and create a FREE account to easily edit your clip.",
  );

  const buttonText = t("buttonText", "Go To Allstar.gg");

  const buttonLink = buildLink({
    url: `https://allstar.gg/register?utm_source=${
      clip.developer?.slug ||
      new URLSearchParams(window.location.search).get("platform") ||
      "unknown"
    }`,
    platform: platform,
    game: getMatchHistoryGameEnum(clip.game),
  });

  if (activeOverlayPopUp !== "edit") return null;

  return (
    <StyledContainer className="edit-popup">
      <StyledCloseButton onClick={() => setActiveOverlayPopUp(null)} />
      <StyledTextContainer className="edit-text-container">
        <StyledTitle className="edit-title" variant="text.titleXSmall">
          {title}
        </StyledTitle>
        <StyledText className="edit-text" variant="text.paragraphMedium">
          {copy}
        </StyledText>
        <StyledButton
          className="edit-button"
          size="small"
          onClick={() => {
            track("Button - Click", {
              locationIdentifier: "edit_popup_button",
              location: "embeddable_player_overlay",
            });
            window.open(buttonLink, "_blank");
          }}
        >
          {buttonText}
          <LaunchIcon />
        </StyledButton>
      </StyledTextContainer>
    </StyledContainer>
  );
};

const StyledCloseButton = styled(CloseButton)`
  position: absolute;
  right: 10px;
  top: 10px;
`;

const StyledButton = styled(Button)`
  align-items: center;
  border-radius: 99px;
  display: flex;
  font-size: 10px;
  gap: 5px;
  height: 25px;
  justify-content: center;
  width: 100%;
`;
const LaunchIcon = styled(MdLaunch)`
  color: ${({ theme }) => theme.colors.ice};
  height: 16px;
  width: 16px;
`;

const StyledText = styled(Text)`
  font-size: 8px;
  line-height: 12px;
`;

const StyledTitle = styled(Text)`
  font-size: 12px;
  line-height: 12px;

  span {
    color: ${({ theme }) => theme.colors.envy};
  }
`;

const StyledContainer = styled(Flex)`
  align-items: center;
  backdrop-filter: blur(4px);
  background-color: rgba(0, 0, 0, 0.75);
  border-radius: 12px;
  flex-direction: column;
  gap: 6px;
  justify-content: center;
  margin: 0 auto;
  padding: 12px;
  pointer-events: auto;
  position: relative;
  text-align: center;
  width: 200px;
`;

const StyledTextContainer = styled(Flex)`
  align-items: center;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  text-align: center;
  width: 100%;
`;
